import { useState } from "react";
import { format, formatDistanceToNowStrict, isValid, parseISO } from "date-fns";
import { GitPullRequest, Plus, Trash2, UserRound } from "lucide-react";

import { cn } from "@monorepo-apps/ui/src/lib/utils";

import { DiffBlock } from "@/presentation/components/board/board-activity-feed";
import { ITERATION_STATUS_LABEL, KIND_LABEL, PRIORITY_LABEL } from "@/presentation/data/b2c-board";
import { DELIVERY_STATUS_LABEL } from "@/presentation/data/board";
import type {
  B2cActivityRow,
  B2cStageRow,
  B2cTaskKind,
  B2cTaskPriority,
  B2cTaskStatus,
  IterationStatus,
} from "@/infrastructure/supabase/b2c-board.types";

interface B2cActivityFeedProps {
  entries: B2cActivityRow[];
  isLoading: boolean;
  /** The read failed — say so, rather than "No activity yet." */
  isError?: boolean;
  stagesByKey: Map<string, B2cStageRow>;
  /** Show what each row is about (whole-board feed); a task's own feed omits it. */
  showSubject?: boolean;
  onOpenTask?: (taskId: string) => void;
  /** Keyset pagination — more older rows are available. */
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const FIELD_LABEL: Record<string, string> = {
  title: "Title",
  description: "Description",
  status: "Status",
  priority: "Priority",
  kind: "Kind",
  is_e2e_proof: "End-to-end proof",
  acceptance_md: "Acceptance",
  notes: "Notes",
  branch_name: "Branch",
  pr_url: "PR",
  workstream: "Workstream",
  iteration: "Iteration",
  goal: "Goal",
  output_url: "Output",
  branch: "Branch",
  merged_at: "Merged",
  summary: "Summary",
  owner_name: "Owner",
  stage_key: "Stage",
  e2e_proof: "End-to-end proof",
  gate_rule: "Gate rule",
  start_date: "Start",
  end_date: "End",
};

// Long free-text fields: show "updated" with a diff toggle, not an old → new dump.
const LONG_FIELDS = new Set([
  "description",
  "acceptance_md",
  "notes",
  "goal",
  "summary",
  "e2e_proof",
  "gate_rule",
]);

const LINK_FIELDS = new Set(["pr_url", "branch_name", "branch", "merged_at", "output_url"]);

/** The B2C board's audit trail (migration 0145), in the MVP board feed's style. */
export function B2cActivityFeed({
  entries,
  isLoading,
  isError = false,
  stagesByKey,
  showSubject = false,
  onOpenTask,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: B2cActivityFeedProps) {
  if (isLoading) {
    return <p className="text-muted-foreground px-1 py-3 text-[12px] italic">Loading activity…</p>;
  }
  if (isError && entries.length === 0) {
    return (
      <p className="text-muted-foreground px-1 py-3 text-[12px] italic">
        Couldn’t load the activity.
      </p>
    );
  }
  if (entries.length === 0) {
    return <p className="text-muted-foreground px-1 py-3 text-[12px] italic">No activity yet.</p>;
  }

  return (
    <ol className="flex flex-col">
      {entries.map((e) => (
        <ActivityRow
          key={e.id}
          e={e}
          stagesByKey={stagesByKey}
          showSubject={showSubject}
          onOpenTask={onOpenTask}
        />
      ))}
      {hasMore ? (
        <li className="pt-2">
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="text-muted-foreground hover:text-foreground hover:bg-muted/50 w-full rounded-sm py-1.5 text-[12px] transition-colors disabled:opacity-60"
          >
            {isLoadingMore ? "Loading…" : "Load more"}
          </button>
        </li>
      ) : null}
    </ol>
  );
}

function ActivityRow({
  e,
  stagesByKey,
  showSubject,
  onOpenTask,
}: {
  e: B2cActivityRow;
  stagesByKey: Map<string, B2cStageRow>;
  showSubject: boolean;
  onOpenTask?: (taskId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const isLong = e.action === "update" && !!e.field && LONG_FIELDS.has(e.field);
  const label = e.field ? (FIELD_LABEL[e.field] ?? e.field) : "field";
  const taskId = e.task_id;

  return (
    <li className="border-border/50 flex items-start gap-2.5 border-b py-2.5 last:border-b-0">
      <ActionIcon e={e} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          {showSubject ? (
            taskId && e.action !== "delete" ? (
              <button
                type="button"
                onClick={() => onOpenTask?.(taskId)}
                className="font-data text-primary shrink-0 text-[10.5px] tabular-nums hover:underline"
              >
                {e.subject}
              </button>
            ) : (
              <span className="font-data text-muted-foreground shrink-0 text-[10.5px] tabular-nums">
                {e.subject}
              </span>
            )
          ) : null}
          <span className="text-foreground min-w-0 text-[12.5px] leading-snug break-words">
            {isLong ? (
              <>
                <span className="font-medium">{label}</span>{" "}
                <button
                  type="button"
                  onClick={() => setOpen((o) => !o)}
                  aria-expanded={open}
                  className="text-primary text-[11.5px] hover:underline"
                >
                  {open ? "hide diff" : "show diff"}
                </button>
              </>
            ) : (
              renderDetail(e, stagesByKey, showSubject)
            )}
          </span>
        </div>
        <div className="mt-0.5 flex items-center gap-1.5">
          <span className="text-muted-foreground truncate text-[11px] font-medium">{e.actor}</span>
          <span className="text-muted-foreground/50 text-[10px]">·</span>
          <time
            className="text-muted-foreground/70 text-[10.5px]"
            dateTime={e.created_at}
            title={new Date(e.created_at).toLocaleString()}
          >
            {formatDistanceToNowStrict(new Date(e.created_at), { addSuffix: true })}
          </time>
        </div>
        {isLong && open ? (
          <div className="mt-2 flex flex-col gap-1.5">
            <DiffBlock tone="removed" value={e.old_value} />
            <DiffBlock tone="added" value={e.new_value} />
          </div>
        ) : null}
      </div>
    </li>
  );
}

function ActionIcon({ e }: { e: B2cActivityRow }) {
  const base = "mt-0.5 size-3.5 shrink-0";
  if (e.action === "create") return <Plus className={cn(base, "text-[var(--rag-green)]")} />;
  if (e.action === "delete") return <Trash2 className={cn(base, "text-[var(--rag-red)]")} />;
  if (e.field && LINK_FIELDS.has(e.field))
    return <GitPullRequest className={cn(base, "text-primary")} />;
  if (e.field === "owner_name") return <UserRound className={cn(base, "text-muted-foreground")} />;
  return <span className={cn(base, "bg-muted-foreground/40 mt-1.5 size-1.5 rounded-full")} />;
}

/** A stored value as people read it. */
function pretty(
  e: B2cActivityRow,
  value: string | null,
  stagesByKey: Map<string, B2cStageRow>
): string {
  if (value === null || value === "") return "—";
  switch (e.field) {
    case "status":
      return e.entity_type === "iteration"
        ? (ITERATION_STATUS_LABEL[value as IterationStatus] ?? value)
        : (DELIVERY_STATUS_LABEL[value as B2cTaskStatus] ?? value);
    case "priority":
      return PRIORITY_LABEL[value as B2cTaskPriority] ?? value;
    case "kind":
      return KIND_LABEL[value as B2cTaskKind] ?? value;
    case "is_e2e_proof":
      return value === "true" ? "yes" : "no";
    case "iteration":
      return `Iteration ${value}`;
    case "stage_key": {
      const stage = stagesByKey.get(value);
      return stage ? `Stage ${stage.number}` : value;
    }
    // Stored as timestamptz::text ("2026-10-19 16:07:00.4+00"), which not every
    // engine's Date parser accepts; parseISO does, and a bad value stays raw.
    case "merged_at": {
      const at = parseISO(value);
      return isValid(at) ? format(at, "d MMM yyyy, HH:mm") : value;
    }
    default:
      return value;
  }
}

function renderDetail(
  e: B2cActivityRow,
  stagesByKey: Map<string, B2cStageRow>,
  showSubject: boolean
) {
  if (e.action !== "update") {
    // The summary starts with the subject; the chip already shows it.
    const prefix = `${e.subject} `;
    const text =
      showSubject && e.summary.startsWith(prefix) ? e.summary.slice(prefix.length) : e.summary;
    return <span>{text}</span>;
  }
  const label = e.field ? (FIELD_LABEL[e.field] ?? e.field) : "field";
  // A branch's merge is a one-way event: say so rather than "— → <timestamp>".
  if (e.field === "merged_at" && e.new_value) {
    return (
      <span>
        <span className="font-medium">merged</span>{" "}
        <span className="text-muted-foreground">{pretty(e, e.new_value, stagesByKey)}</span>
      </span>
    );
  }
  return (
    <span>
      <span className="font-medium">{label}</span>{" "}
      <span className="text-muted-foreground">{pretty(e, e.old_value, stagesByKey)}</span>
      <span className="text-muted-foreground/60"> → </span>
      <span>{pretty(e, e.new_value, stagesByKey)}</span>
    </span>
  );
}
